import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/Textarea";  
import { Card, CardContent } from "./ui/card";  // CardHeader, CardTitleを削除
import { Badge } from "./ui/badge";
import { X, Plus, Save, Eye, Settings } from "lucide-react";

export default function ProcedureStepEditor() {
  const [step, setStep] = useState({
    title: "清潔野展開",
    description: "ガーゼセット→大判→開創器展開",
    scrubNotes: "腸鉗子と開創器のタイミングに注意",
    circulatorNotes: "体位と電気メスコード配置の確認",
    instruments: ["開創器（赤バイ）", "腸鉗子（アトラ）"],
    materials: {
      caseCart: ["基本セット", "縫合糸（3-0）"],
      standby: ["開創器（長短）"],
      open: ["抗生剤溶解液"],
      warehouse: ["腹腔鏡トロッカー"],
      borrowed: ["プレートXX"]
    },
    xrHint: "左視野中央（優先度：2）"
  });

  const [viewMode, setViewMode] = useState("edit"); // edit, scrub, circulator

  const materialCategories = {
    caseCart: { label: "✅ 症例カート搭載品", color: "bg-green-100 text-green-800" },
    standby: { label: "🟡 スタンバイ器械", color: "bg-yellow-100 text-yellow-800" },
    open: { label: "🟠 開ける材料", color: "bg-orange-100 text-orange-800" },
    warehouse: { label: "🔵 倉庫準備品", color: "bg-blue-100 text-blue-800" },
    borrowed: { label: "🔴 医在庫（借用材料）", color: "bg-red-100 text-red-800" }
  };

  const addItem = (category, newItem) => {
    if (newItem.trim()) {
      setStep({
        ...step,
        materials: {
          ...step.materials,
          [category]: [...step.materials[category], newItem.trim()]
        }
      });
    }
  };

  const removeItem = (category, index) => {
    setStep({
      ...step,
      materials: {
        ...step.materials,
        [category]: step.materials[category].filter((_, i) => i !== index)
      }
    });
  };

  const addInstrument = (newInstrument) => {
    if (newInstrument.trim()) {
      setStep({
        ...step,
        instruments: [...step.instruments, newInstrument.trim()]
      });
    }
  };

  const removeInstrument = (index) => {
    setStep({
      ...step,
      instruments: step.instruments.filter((_, i) => i !== index)
    });
  };

  // ロール別表示モード
  const renderViewMode = () => {
    if (viewMode === "scrub") {
      return (
        <Card className="bg-blue-50">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-blue-600">🧤 器械出しビュー</h2>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg">{step.title}</h3>
                <p className="text-gray-700 mt-2">{step.description}</p>
              </div>
              
              <div className="bg-blue-100 p-3 rounded">
                <h4 className="font-medium text-blue-800">⚠️ 注意点</h4>
                <p className="text-blue-700">{step.scrubNotes}</p>
              </div>

              <div>
                <h4 className="font-medium mb-2">使用器械</h4>
                <div className="flex flex-wrap gap-2">
                  {step.instruments.map((instrument, index) => (
                    <Badge key={index} className="bg-gray-100 text-gray-800">
                      {instrument}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">準備する材料</h4>
                {Object.entries(step.materials).map(([category, items]) => 
                  items.length > 0 && (
                    <div key={category} className="mb-2">
                      <span className={`text-xs px-2 py-1 rounded ${materialCategories[category].color}`}>
                        {materialCategories[category].label}
                      </span>
                      <div className="ml-4 mt-1">
                        {items.map((item, index) => (
                          <div key={index} className="text-sm">• {item}</div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (viewMode === "circulator") {
      return (
        <Card className="bg-green-50">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-green-600">🔄 外回りビュー</h2>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg">{step.title}</h3>
                <p className="text-gray-700 mt-2">{step.description}</p>
              </div>
              
              <div className="bg-green-100 p-3 rounded">
                <h4 className="font-medium text-green-800">📋 準備・確認事項</h4>
                <p className="text-green-700">{step.circulatorNotes}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(step.materials).map(([category, items]) => 
                  items.length > 0 && (
                    <div key={category} className="border rounded p-3">
                      <div className={`text-sm px-2 py-1 rounded mb-2 inline-block ${materialCategories[category].color}`}>
                        {materialCategories[category].label}
                      </div>
                      {items.map((item, index) => (
                        <div key={index} className="text-sm mb-1">• {item}</div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // 編集モード
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">手順編集：{step.title}</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Save className="w-4 h-4 mr-1" />
                  保存
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-medium">工程タイトル</label>
              <Input 
                value={step.title} 
                onChange={e => setStep({ ...step, title: e.target.value })} 
              />
            </div>

            <div className="space-y-2">
              <label className="block font-medium">手順内容</label>
              <Textarea 
                value={step.description} 
                onChange={e => setStep({ ...step, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block font-medium text-blue-600">🧤 器械出し向けメモ</label>
                <Textarea 
                  value={step.scrubNotes} 
                  onChange={e => setStep({ ...step, scrubNotes: e.target.value })}
                  rows={3}
                  className="border-blue-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block font-medium text-green-600">🔄 外回り向けメモ</label>
                <Textarea 
                  value={step.circulatorNotes} 
                  onChange={e => setStep({ ...step, circulatorNotes: e.target.value })}
                  rows={3}
                  className="border-green-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-medium">使用器械</label>
              <div className="space-y-2">
                {step.instruments.map((instrument, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input 
                      value={instrument}
                      onChange={e => {
                        const newInstruments = [...step.instruments];
                        newInstruments[index] = e.target.value;
                        setStep({ ...step, instruments: newInstruments });
                      }}
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => removeInstrument(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Input 
                    placeholder="新しい器械を追加..."
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        addInstrument(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={e => {
                      const input = e.target.parentElement.querySelector('input');
                      addInstrument(input.value);
                      input.value = '';
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block font-medium">材料分類</label>
              {Object.entries(materialCategories).map(([category, config]) => (
                <div key={category} className="border rounded p-4">
                  <div className={`text-sm px-2 py-1 rounded mb-3 inline-block ${config.color}`}>
                    {config.label}
                  </div>
                  <div className="space-y-2">
                    {step.materials[category].map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input 
                          value={item}
                          onChange={e => {
                            const newMaterials = { ...step.materials };
                            newMaterials[category][index] = e.target.value;
                            setStep({ ...step, materials: newMaterials });
                          }}
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => removeItem(category, index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input 
                        placeholder="新しい材料を追加..."
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            addItem(category, e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={e => {
                          const input = e.target.parentElement.querySelector('input');
                          addItem(category, input.value);
                          input.value = '';
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <label className="block font-medium">XR表示ヒント</label>
              <Input 
                value={step.xrHint} 
                onChange={e => setStep({ ...step, xrHint: e.target.value })}
                placeholder="例: 左視野中央（優先度：2）"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-8 space-y-4">
      {/* ビュー切り替えボタン */}
      <div className="flex justify-center gap-2">
        <Button 
          variant={viewMode === "edit" ? "default" : "outline"}
          onClick={() => setViewMode("edit")}
          size="sm"
        >
          <Settings className="w-4 h-4 mr-1" />
          編集
        </Button>
        <Button 
          variant={viewMode === "scrub" ? "default" : "outline"}
          onClick={() => setViewMode("scrub")}
          size="sm"
        >
          🧤 器械出し
        </Button>
        <Button 
          variant={viewMode === "circulator" ? "default" : "outline"}
          onClick={() => setViewMode("circulator")}
          size="sm"
        >
          🔄 外回り
        </Button>
      </div>

      {renderViewMode()}
    </div>
  );
}